import axios from 'axios';

// By using an empty string, fetch('/api/auth/login') will automatically point to the current domain.
// Locally: http://localhost:3000/api/auth/login (which React proxies to 8082)
// Production: https://memora.mozanlabs.in/api/auth/login (which Nginx proxies to 8082)
const BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({ baseURL: BASE_URL });

// Attach JWT on every request
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// ── Auto-refresh on 401 ───────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue  = [];

const processQueue = (error, newToken = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    error ? reject(error) : resolve(newToken);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;

    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;

      if (isRefreshing) {
        // Another request already refreshing — queue and wait
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(newToken => {
          original.headers.Authorization = `Bearer ${newToken}`;
          return api(original);
        });
      }

      isRefreshing = true;
      try {
        // Refresh token is in the HttpOnly cookie — browser sends it automatically
        const { data } = await axios.post(
          `${BASE_URL}/api/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const newToken = data.accessToken;
        localStorage.setItem('token', newToken);
        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);

        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);

      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

// ── Auth (use plain axios with withCredentials for cookie handling) ────────
export const authApi = {
  signup:    (data) => axios.post(`${BASE_URL}/api/auth/signup`,    data, { withCredentials: true }),
  sendOtp:   (data) => axios.post(`${BASE_URL}/api/auth/send-otp`,   data, { withCredentials: true }),
  verifyOtp: (data) => axios.post(`${BASE_URL}/api/auth/verify-otp`, data, { withCredentials: true }),
  login:     (data) => axios.post(`${BASE_URL}/api/auth/login`,     data, { withCredentials: true }),
  refresh:   ()     => axios.post(`${BASE_URL}/api/auth/refresh`,   {},   { withCredentials: true }),
  logout:    ()     => axios.post(`${BASE_URL}/api/auth/logout`,    {},   { withCredentials: true }),
};

// ── Documents ─────────────────────────────────────────────────────────────
export const docsApi = {
  create:         (data)                => api.post('/api/documents', data),
  getById:        (id)                  => api.get(`/api/documents/${id}`),
  update:         (id, data)            => api.patch(`/api/documents/${id}`, data),
  delete:         (id)                  => api.delete(`/api/documents/${id}`),
  search:         (data)                => api.post('/api/documents/search', data),
  leastImportant: (limit = 20)          => api.get(`/api/documents/least-important?limit=${limit}`),
  history:        (id, page=0, size=50) => api.get(`/api/documents/${id}/history?page=${page}&size=${size}`),
};

export default api;
